import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ArrowLeft, ArrowRight, Check } from 'lucide-react';
import SchoolLogo from '../SchoolLogo';
import SchoolProfileFooter from '../SchoolProfileFooter';
import {
  ADMISSION_STEPS,
  DetailsStep,
  MedicalStep,
  ParentsStep,
  ReviewStep,
  StudentStep,
} from './AdmissionSteps';
import { emptyAdmissionValues, validateStep } from '../../../utils/admissionValidation';
import {
  AdmissionSchoolNotFoundError,
  AdmissionValidationError,
  submitPublicAdmission,
} from '../../../services/admissionApi';
import { getSchoolThemeStyle } from '../../../utils/schoolTheme';
import { resetDocumentMetadata, setDocumentMetadata } from '../../../utils/documentMeta';
import '../schoolProfile.css';
import './admissionForm.css';

/**
 * The public admission application: five steps, then a confirmation.
 *
 * Everything the parent types stays in this component until the backend
 * confirms the submission, so a failed attempt never loses their answers and a
 * successful one never leaves them on a half-filled form.
 */
const AdmissionFormPage = ({ profile, onBackToProfile }) => {
  const { schoolName, publicPath, academic } = profile;

  const [stepIndex, setStepIndex] = useState(0);
  const [values, setValues] = useState(emptyAdmissionValues);
  const [errors, setErrors] = useState({});
  const [profileImage, setProfileImage] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [status, setStatus] = useState('editing'); // editing | submitting | submitted
  const [submitError, setSubmitError] = useState(null);

  const headingRef = useRef(null);
  const errorSummaryRef = useRef(null);
  const step = ADMISSION_STEPS[stepIndex];
  const isReview = step.id === 'review';

  useEffect(() => {
    setDocumentMetadata({
      title: `Apply to ${schoolName} | AtadaBoard`,
      description: `Start an admission application for ${schoolName}.`,
      robots: 'noindex, follow',
    });
    return resetDocumentMetadata;
  }, [schoolName]);

  // Object URLs must be released or the preview leaks for the life of the page.
  const profileImagePreview = useMemo(
    () => (profileImage ? URL.createObjectURL(profileImage) : null),
    [profileImage]
  );
  useEffect(
    () => () => {
      if (profileImagePreview) URL.revokeObjectURL(profileImagePreview);
    },
    [profileImagePreview]
  );

  const setValue = useCallback((key, value) => {
    setValues((previous) => ({ ...previous, [key]: value }));
    // Clear the message as soon as the parent starts fixing the field.
    setErrors((previous) => {
      if (!previous[key]) return previous;
      const next = { ...previous };
      delete next[key];
      return next;
    });
  }, []);

  const files = { profileImage, profileImagePreview, documents };
  const setFiles = useMemo(
    () => ({
      setProfileImage,
      addDocuments: (added) => setDocuments((previous) => [...previous, ...added]),
      removeDocument: (id) =>
        setDocuments((previous) => previous.filter((item) => item.id !== id)),
    }),
    []
  );

  const goToStep = useCallback((index) => {
    setStepIndex(index);
    setSubmitError(null);
    // Move focus to the new step so keyboard and screen reader users land in
    // the right place rather than at the top of the document.
    window.requestAnimationFrame(() => {
      if (headingRef.current) headingRef.current.focus();
    });
  }, []);

  const handleNext = () => {
    const stepErrors = validateStep(step.id, values);
    setErrors(stepErrors);

    if (Object.keys(stepErrors).length > 0) {
      window.requestAnimationFrame(() => {
        if (errorSummaryRef.current) errorSummaryRef.current.focus();
      });
      return;
    }
    goToStep(Math.min(stepIndex + 1, ADMISSION_STEPS.length - 1));
  };

  const handleBack = () => goToStep(Math.max(stepIndex - 1, 0));

  const handleEditStep = (stepId) => {
    const index = ADMISSION_STEPS.findIndex((item) => item.id === stepId);
    if (index >= 0) goToStep(index);
  };

  const handleSubmit = async (event) => {
    if (event) event.preventDefault();
    // The form's onSubmit is only a fallback for implicit submission (Enter in
    // a field). Nothing may be sent before the parent has reached the review
    // step and asked for it.
    if (!isReview || status === 'submitting') return;

    const stepErrors = validateStep('review', values);
    setErrors(stepErrors);
    if (Object.keys(stepErrors).length > 0) {
      setSubmitError('Some answers still need checking. Use Edit to fix them, then submit again.');
      window.requestAnimationFrame(() => {
        if (errorSummaryRef.current) errorSummaryRef.current.focus();
      });
      return;
    }

    setStatus('submitting');
    setSubmitError(null);

    try {
      await submitPublicAdmission(publicPath, values, { profileImage, documents });
      setStatus('submitted');
      window.scrollTo({ top: 0, behavior: 'auto' });
    } catch (error) {
      // The form keeps every answer: the parent can fix and resubmit.
      setStatus('editing');
      if (error instanceof AdmissionSchoolNotFoundError) {
        setSubmitError(error.message);
      } else if (error instanceof AdmissionValidationError) {
        setSubmitError(error.message);
      } else {
        setSubmitError(
          error && error.message
            ? error.message
            : 'We could not submit this application. Please try again.'
        );
      }
      window.requestAnimationFrame(() => {
        if (errorSummaryRef.current) errorSummaryRef.current.focus();
      });
    }
  };

  const errorList = Object.entries(errors);
  const themeStyle = getSchoolThemeStyle(profile);

  if (status === 'submitted') {
    return (
      <div className="sp-page ad-page" style={themeStyle}>
        <main className="sp-shell ad-confirmation">
          <span className="ad-confirmation-mark" aria-hidden="true">
            <Check size={26} strokeWidth={2} />
          </span>
          <p className="sp-label">Application received</p>
          <h1 className="sp-display sp-display-m ad-confirmation-title">
            Thank you, {schoolName} has your application
          </h1>
          <p className="sp-standfirst">
            The school will review it and contact you on{' '}
            <strong className="ad-contact-echo">{values.parentPhone}</strong>
            {values.parentEmail ? (
              <>
                {' '}
                or at <strong className="ad-contact-echo">{values.parentEmail}</strong>
              </>
            ) : null}
            .
          </p>

          <div className="sp-actions ad-confirmation-actions">
            <button type="button" className="sp-btn sp-btn-primary" onClick={onBackToProfile}>
              Back to {schoolName}
            </button>
          </div>
        </main>
        <SchoolProfileFooter schoolName={schoolName} />
      </div>
    );
  }

  return (
    <div className="sp-page ad-page" style={themeStyle}>
      <header className="ad-masthead">
        <div className="sp-shell ad-masthead-inner">
          <button type="button" className="ad-back-link" onClick={onBackToProfile}>
            <ArrowLeft size={16} strokeWidth={1.75} aria-hidden="true" />
            Back to {schoolName}
          </button>
          <SchoolLogo schoolName={schoolName} logoUrl={profile.logoUrl} size={48} />
        </div>
      </header>

      <main className="sp-shell ad-main">
        <p className="sp-label">Admission application</p>
        <h1 className="sp-display sp-display-m ad-title">Apply to {schoolName}</h1>
        {academic && academic.currentYear > 0 && (
          <p className="ad-academic">
            {academic.useAcademicYear
              ? `Academic year ${academic.currentYear}/${academic.currentYear + 1}`
              : `${academic.currentYear} school year`}
            {academic.currentQuarter > 0 ? ` · Term ${academic.currentQuarter}` : ''}
          </p>
        )}

        <nav className="ad-progress" aria-label="Application progress">
          <ol className="ad-progress-list">
            {ADMISSION_STEPS.map((item, index) => {
              const state =
                index === stepIndex ? 'current' : index < stepIndex ? 'done' : 'upcoming';
              return (
                <li className="ad-progress-step" data-state={state} key={item.id}>
                  <button
                    type="button"
                    className="ad-progress-button"
                    // Only steps already reached can be jumped to, so nobody
                    // skips validation by clicking ahead.
                    disabled={index > stepIndex}
                    aria-current={index === stepIndex ? 'step' : undefined}
                    onClick={() => goToStep(index)}
                  >
                    <span className="ad-progress-index" aria-hidden="true">
                      {index < stepIndex ? <Check size={13} strokeWidth={2.5} /> : index + 1}
                    </span>
                    <span className="ad-progress-title">{item.title}</span>
                  </button>
                </li>
              );
            })}
          </ol>
          <p className="sp-visually-hidden" aria-live="polite">
            Step {stepIndex + 1} of {ADMISSION_STEPS.length}: {step.heading}
          </p>
        </nav>

        <form className="ad-form" onSubmit={handleSubmit} noValidate>
          <h2 className="ad-step-heading" tabIndex={-1} ref={headingRef}>
            {step.heading}
          </h2>

          {(errorList.length > 0 || submitError) && (
            <div
              className="ad-error-summary"
              role="alert"
              tabIndex={-1}
              ref={errorSummaryRef}
            >
              {submitError && <p className="ad-error-summary-title">{submitError}</p>}
              {errorList.length > 0 && (
                <>
                  <p className="ad-error-summary-title">
                    {errorList.length === 1
                      ? 'One answer needs checking:'
                      : `${errorList.length} answers need checking:`}
                  </p>
                  <ul>
                    {errorList.map(([field, message]) => (
                      <li key={field}>
                        <a href={`#${field}`}>{message}</a>
                      </li>
                    ))}
                  </ul>
                </>
              )}
            </div>
          )}

          <div className="ad-step-body">
            {step.id === 'student' && (
              <StudentStep
                values={values}
                errors={errors}
                setValue={setValue}
                files={files}
                setFiles={setFiles}
              />
            )}
            {step.id === 'medical' && (
              <MedicalStep values={values} errors={errors} setValue={setValue} />
            )}
            {step.id === 'parents' && (
              <ParentsStep values={values} errors={errors} setValue={setValue} />
            )}
            {step.id === 'details' && (
              <DetailsStep
                values={values}
                errors={errors}
                setValue={setValue}
                files={files}
                setFiles={setFiles}
              />
            )}
            {isReview && (
              <ReviewStep
                values={values}
                files={files}
                schoolName={schoolName}
                onEditStep={handleEditStep}
              />
            )}
          </div>

          <div className="ad-form-actions">
            {stepIndex > 0 ? (
              <button type="button" className="sp-btn sp-btn-outline" onClick={handleBack}>
                <ArrowLeft size={17} strokeWidth={1.75} aria-hidden="true" />
                Back
              </button>
            ) : (
              <span />
            )}

            {/*
              Both buttons are type="button" and carry distinct keys on purpose.
              They sit at the same position in the tree, so without a key React
              would reuse one DOM node and merely flip its type attribute. The
              browser runs a click's default action *after* that mutation, so
              clicking Continue on the last step would land on a node that had
              become a submit button and post the application on the spot.
              Separate keys force a fresh node, and keeping type="button" means
              no click can ever submit the form implicitly.
            */}
            {isReview ? (
              <button
                key="submit-application"
                type="button"
                className="sp-btn sp-btn-primary"
                onClick={handleSubmit}
                disabled={status === 'submitting'}
              >
                {status === 'submitting' ? 'Submitting…' : 'Submit application'}
              </button>
            ) : (
              <button
                key="continue-step"
                type="button"
                className="sp-btn sp-btn-primary"
                onClick={handleNext}
              >
                Continue
                <ArrowRight size={17} strokeWidth={1.75} aria-hidden="true" />
              </button>
            )}
          </div>
        </form>
      </main>

      <SchoolProfileFooter schoolName={schoolName} />
    </div>
  );
};

export default AdmissionFormPage;
